// =============================================================================
// BROKERAGE MANAGEMENT COMPONENT (SSoT-Based)
// =============================================================================
// Admin UI for managing the brokerage system using Unit-level SSoT.
// 
// Features:
// - Inflow tiles: ACCRUED / INVOICED / PAID
// - Outflow tiles: ELIGIBLE / INVOICED / APPROVED / PAID
// - Per-broker tracking with independent status
// - Generate developer invoices
// - Mark developer payments
// - Approve/pay broker invoices
// - By-unit breakdown view
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import {
  ArrowDownRight,
  ArrowUpRight,
  Building2,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  CreditCard,
  FileText,
  RefreshCw,
  Send,
  Users,
  AlertTriangle,
  Wallet,
  Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

const formatINR = (val) => {
  if (!val && val !== 0) return '₹0';
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(2)} L`;
  return `₹${val.toLocaleString('en-IN')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

// =============================================================================
// STATUS BADGES
// =============================================================================

const STATUS_STYLES = {
  // Inflow statuses
  PENDING: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  ACCRUED: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  RERA_PAYMENT_DONE: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  DEV_INVOICED: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  DEV_BROKERAGE_PAID: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  INVOICED: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  PARTIAL: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  PAID: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  
  // Outflow statuses
  ELIGIBLE: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  APPROVED: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  COMPLETE: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  
  // Allocation statuses
  BOOKED: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

const StatusBadge = ({ status }) => (
  <span className={`px-2 py-1 text-xs font-medium border ${STATUS_STYLES[status] || STATUS_STYLES.PENDING}`}>
    {status}
  </span>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function BrokerageManagement({ token }) {
  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data
  const [summary, setSummary] = useState({ inflow: {}, outflow: {} });
  const [inflowUnits, setInflowUnits] = useState([]);
  const [outflowAllocations, setOutflowAllocations] = useState([]);
  const [developerInvoices, setDeveloperInvoices] = useState([]);
  
  // Modal state
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [showDevPayModal, setShowDevPayModal] = useState(false);
  const [showReraPaymentModal, setShowReraPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ reference: '', amount: '' });
  const [reraPaymentForm, setReraPaymentForm] = useState({ reference: '', amount: '', notes: '' });
  const [processingId, setProcessingId] = useState(null);
  
  // Expanded units in By-Unit view
  const [expandedUnits, setExpandedUnits] = useState({});

  // ==========================================================================
  // DATA FETCHING
  // ==========================================================================

  const fetchData = useCallback(async () => {
    try {
      setRefreshing(true);
      
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      };
      
      const [summaryRes, inflowRes, outflowRes, devInvoicesRes] = await Promise.all([
        fetch(`${API}/api/admin/brokerage/summary`, { headers }),
        fetch(`${API}/api/admin/brokerage/inflow`, { headers }),
        fetch(`${API}/api/admin/brokerage/outflow`, { headers }),
        fetch(`${API}/api/admin/developer-invoices`, { headers })
      ]);
      
      const [summaryData, inflowData, outflowData, devInvoicesData] = await Promise.all([
        summaryRes.json(),
        inflowRes.json(),
        outflowRes.json(),
        devInvoicesRes.json()
      ]);
      
      setSummary(summaryData);
      setInflowUnits(inflowData.units || []);
      setOutflowAllocations(outflowData.allocations || []);
      setDeveloperInvoices(devInvoicesData.invoices || []);
      
    } catch (error) {
      console.error('Error fetching brokerage data:', error);
      toast.error('Failed to load brokerage data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ==========================================================================
  // ACTIONS
  // ==========================================================================

  const confirmReraPayment = async () => {
    if (!selectedUnit || !reraPaymentForm.reference || !reraPaymentForm.amount) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setProcessingId(selectedUnit.unitId);
    try {
      const res = await fetch(`${API}/api/admin/brokerage/inflow/${selectedUnit.unitId}/confirm-rera-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({
          paymentReference: reraPaymentForm.reference,
          paymentAmount: parseFloat(reraPaymentForm.amount),
          notes: reraPaymentForm.notes || undefined
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to confirm RERA payment');
      }
      
      toast.success('RERA payment confirmed. Unit is now ready for developer invoice.');
      setShowReraPaymentModal(false);
      setSelectedUnit(null);
      setReraPaymentForm({ reference: '', amount: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessingId(null);
    }
  };

  const generateDevInvoice = async (unitId) => {
    setProcessingId(unitId);
    try {
      const res = await fetch(`${API}/api/admin/brokerage/inflow/${unitId}/generate-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({})
      });
      
      // Clone response before reading to avoid "body disturbed" error
      const resClone = res.clone();
      
      if (!res.ok) {
        try {
          const error = await resClone.json();
          const message = typeof error.detail === 'object' 
            ? error.detail.message || 'Failed to generate invoice'
            : error.detail || 'Failed to generate invoice';
          throw new Error(message);
        } catch (parseError) {
          throw new Error('Failed to generate invoice');
        }
      }
      
      toast.success('Developer invoice generated');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessingId(null);
    }
  };

  const markDevPaid = async () => {
    if (!selectedUnit || !paymentForm.reference || !paymentForm.amount) {
      toast.error('Please fill all fields');
      return;
    }
    
    const invoiceId = selectedUnit.brokerage?.inflow?.invoiceId;
    if (!invoiceId) {
      toast.error('No invoice found for this unit');
      return;
    }
    
    setProcessingId(invoiceId);
    try {
      const res = await fetch(`${API}/api/admin/brokerage/inflow/${invoiceId}/mark-paid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({
          paymentReference: paymentForm.reference,
          paymentAmount: parseFloat(paymentForm.amount)
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to record payment');
      }
      
      const result = await res.json();
      toast.success(`Developer payment recorded. ${result.brokersEligible} brokers now eligible.`);
      setShowDevPayModal(false);
      setSelectedUnit(null);
      setPaymentForm({ reference: '', amount: '' });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessingId(null);
    }
  };

  const approveBrokerInvoice = async (invoiceId) => {
    setProcessingId(invoiceId);
    try {
      const res = await fetch(`${API}/api/admin/brokerage/outflow/${invoiceId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({})
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to approve');
      }
      
      toast.success('Broker invoice approved');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessingId(null);
    }
  };

  const payBrokerInvoice = async (invoiceId) => {
    const paymentRef = prompt('Enter payment reference (UTR/Transaction ID):');
    if (!paymentRef) return;
    
    setProcessingId(invoiceId);
    try {
      const res = await fetch(`${API}/api/admin/brokerage/outflow/${invoiceId}/pay`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ paymentReference: paymentRef })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to record payment');
      }
      
      toast.success('Broker payment recorded');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessingId(null);
    }
  };

  const recomputeUnit = async (unitId) => {
    setProcessingId(unitId);
    try {
      const res = await fetch(`${API}/api/admin/brokerage/units/${unitId}/recompute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });
      
      if (!res.ok) throw new Error('Failed to recompute');
      
      toast.success('Brokerage state recomputed');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessingId(null);
    }
  };

  // ==========================================================================
  // COMPUTED VALUES
  // ==========================================================================

  // Categorize allocations by status
  const allocationsByStatus = {
    BOOKED: outflowAllocations.filter(a => a.allocation?.outflowStatus === 'BOOKED'),
    ELIGIBLE: outflowAllocations.filter(a => a.allocation?.outflowStatus === 'ELIGIBLE'),
    INVOICED: outflowAllocations.filter(a => a.allocation?.outflowStatus === 'INVOICED'),
    APPROVED: outflowAllocations.filter(a => a.allocation?.outflowStatus === 'APPROVED'),
    PAID: outflowAllocations.filter(a => a.allocation?.outflowStatus === 'PAID'),
  };

  // Categorize inflow units by status
  const unitsByInflowStatus = {
    ACCRUED: inflowUnits.filter(u => u.brokerage?.inflow?.status === 'ACCRUED'),
    RERA_PAYMENT_DONE: inflowUnits.filter(u => u.brokerage?.inflow?.status === 'RERA_PAYMENT_DONE'),
    DEV_INVOICED: inflowUnits.filter(u => u.brokerage?.inflow?.status === 'DEV_INVOICED'),
    DEV_BROKERAGE_PAID: inflowUnits.filter(u => u.brokerage?.inflow?.status === 'DEV_BROKERAGE_PAID'),
    // Keep legacy status mapping for backwards compatibility
    INVOICED: inflowUnits.filter(u => u.brokerage?.inflow?.status === 'INVOICED'),
    PAID: inflowUnits.filter(u => u.brokerage?.inflow?.status === 'PAID'),
  };

  // Calculate totals
  const inflowTotals = {
    ACCRUED: unitsByInflowStatus.ACCRUED.reduce((sum, u) => sum + (u.brokerage?.inflow?.developerOwedAmount || 0), 0),
    RERA_PAYMENT_DONE: unitsByInflowStatus.RERA_PAYMENT_DONE.reduce((sum, u) => sum + (u.brokerage?.inflow?.developerOwedAmount || 0), 0),
    DEV_INVOICED: unitsByInflowStatus.DEV_INVOICED.reduce((sum, u) => sum + (u.brokerage?.inflow?.invoiceAmount || 0), 0),
    DEV_BROKERAGE_PAID: unitsByInflowStatus.DEV_BROKERAGE_PAID.reduce((sum, u) => sum + (u.brokerage?.inflow?.paymentAmount || 0), 0),
    INVOICED: unitsByInflowStatus.INVOICED.reduce((sum, u) => sum + (u.brokerage?.inflow?.invoiceAmount || 0), 0),
    PAID: unitsByInflowStatus.PAID.reduce((sum, u) => sum + (u.brokerage?.inflow?.paymentAmount || 0), 0),
  };

  const outflowTotals = {
    BOOKED: allocationsByStatus.BOOKED.reduce((sum, a) => sum + (a.allocation?.commissionFinal || 0), 0),
    ELIGIBLE: allocationsByStatus.ELIGIBLE.reduce((sum, a) => sum + (a.allocation?.commissionFinal || 0), 0),
    INVOICED: allocationsByStatus.INVOICED.reduce((sum, a) => sum + (a.allocation?.commissionFinal || 0), 0),
    APPROVED: allocationsByStatus.APPROVED.reduce((sum, a) => sum + (a.allocation?.commissionFinal || 0), 0),
    PAID: allocationsByStatus.PAID.reduce((sum, a) => sum + (a.allocation?.commissionFinal || 0), 0),
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin text-white/50" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Brokerage Management</h2>
          <p className="text-sm text-gray-400 mt-1">
            SSoT-based commission tracking • Developer inflow → Broker outflow
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          disabled={refreshing}
          className="border-white/20 text-white hover:bg-white/10"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        {['overview', 'inflow', 'outflow', 'by-unit'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab
                ? 'text-white border-b-2 border-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {tab === 'overview' && 'Overview'}
            {tab === 'inflow' && `Inflow (${Object.values(unitsByInflowStatus).flat().length})`}
            {tab === 'outflow' && `Outflow (${outflowAllocations.length})`}
            {tab === 'by-unit' && 'By Unit'}
          </button>
        ))}
      </div>

      {/* =================================================================== */}
      {/* OVERVIEW TAB */}
      {/* =================================================================== */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Inflow Summary */}
          <div>
            <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
              <ArrowDownRight className="w-4 h-4 text-emerald-400" />
              Developer Inflow (Developer → AltSlice)
            </h3>
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Pending RERA', status: 'ACCRUED', count: unitsByInflowStatus.ACCRUED.length, amount: inflowTotals.ACCRUED, color: 'yellow' },
                { label: 'Ready to Invoice', status: 'RERA_PAYMENT_DONE', count: unitsByInflowStatus.RERA_PAYMENT_DONE.length, amount: inflowTotals.RERA_PAYMENT_DONE, color: 'orange' },
                { label: 'Invoice Sent', status: 'DEV_INVOICED', count: unitsByInflowStatus.DEV_INVOICED.length, amount: inflowTotals.DEV_INVOICED, color: 'purple' },
                { label: 'Payment Received', status: 'DEV_BROKERAGE_PAID', count: unitsByInflowStatus.DEV_BROKERAGE_PAID.length, amount: inflowTotals.DEV_BROKERAGE_PAID, color: 'emerald' },
              ].map((item) => (
                <div key={item.status} className={`bg-${item.color}-500/10 border border-${item.color}-500/20 p-4`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-${item.color}-400 text-xs font-medium`}>{item.label}</span>
                    <span className={`text-${item.color}-400 text-lg font-semibold`}>{item.count}</span>
                  </div>
                  <p className="text-white font-medium">{formatINR(item.amount)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Outflow Summary */}
          <div>
            <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4 text-purple-400" />
              Broker Outflow (AltSlice → Brokers)
            </h3>
            <div className="grid grid-cols-5 gap-4">
              {[
                { label: 'Booked', status: 'BOOKED', count: allocationsByStatus.BOOKED.length, amount: outflowTotals.BOOKED, color: 'gray' },
                { label: 'Eligible', status: 'ELIGIBLE', count: allocationsByStatus.ELIGIBLE.length, amount: outflowTotals.ELIGIBLE, color: 'blue' },
                { label: 'Invoiced', status: 'INVOICED', count: allocationsByStatus.INVOICED.length, amount: outflowTotals.INVOICED, color: 'purple' },
                { label: 'Approved', status: 'APPROVED', count: allocationsByStatus.APPROVED.length, amount: outflowTotals.APPROVED, color: 'cyan' },
                { label: 'Paid', status: 'PAID', count: allocationsByStatus.PAID.length, amount: outflowTotals.PAID, color: 'emerald' },
              ].map((item) => (
                <div key={item.status} className={`bg-${item.color}-500/10 border border-${item.color}-500/20 p-4`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-${item.color}-400 text-xs font-medium`}>{item.label}</span>
                    <span className={`text-${item.color}-400 text-lg font-semibold`}>{item.count}</span>
                  </div>
                  <p className="text-white font-medium">{formatINR(item.amount)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Action Items */}
          <div className="bg-white/5 border border-white/10 p-4">
            <h3 className="text-sm font-medium text-white mb-4">Action Items</h3>
            <div className="space-y-2">
              {unitsByInflowStatus.ACCRUED.length > 0 && (
                <div className="flex items-center gap-3 p-3 bg-yellow-500/10 border border-yellow-500/20">
                  <Clock className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm text-yellow-400">
                    {unitsByInflowStatus.ACCRUED.length} units ready to invoice developer
                  </span>
                  <button
                    onClick={() => setActiveTab('inflow')}
                    className="ml-auto text-xs text-yellow-400 hover:underline"
                  >
                    View →
                  </button>
                </div>
              )}
              {allocationsByStatus.INVOICED.length > 0 && (
                <div className="flex items-center gap-3 p-3 bg-purple-500/10 border border-purple-500/20">
                  <FileText className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-purple-400">
                    {allocationsByStatus.INVOICED.length} broker invoices pending approval
                  </span>
                  <button
                    onClick={() => setActiveTab('outflow')}
                    className="ml-auto text-xs text-purple-400 hover:underline"
                  >
                    Review →
                  </button>
                </div>
              )}
              {allocationsByStatus.APPROVED.length > 0 && (
                <div className="flex items-center gap-3 p-3 bg-cyan-500/10 border border-cyan-500/20">
                  <CreditCard className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm text-cyan-400">
                    {allocationsByStatus.APPROVED.length} broker payments ready
                  </span>
                  <button
                    onClick={() => setActiveTab('outflow')}
                    className="ml-auto text-xs text-cyan-400 hover:underline"
                  >
                    Pay Now →
                  </button>
                </div>
              )}
              {unitsByInflowStatus.ACCRUED.length === 0 && 
               allocationsByStatus.INVOICED.length === 0 && 
               allocationsByStatus.APPROVED.length === 0 && (
                <div className="flex items-center gap-3 p-3 bg-gray-500/10 border border-gray-500/20">
                  <CheckCircle className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-400">
                    No pending actions
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* INFLOW TAB */}
      {/* =================================================================== */}
      {activeTab === 'inflow' && (
        <div className="space-y-6">
          {/* Ready to Invoice (ACCRUED - before RERA payment) */}
          <div className="bg-white/5 border border-white/10">
            <div className="px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-medium text-yellow-400 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Tranche Funded - Pending RERA Payment ({unitsByInflowStatus.ACCRUED.length})
              </h3>
            </div>
            {unitsByInflowStatus.ACCRUED.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No units pending RERA payment
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {unitsByInflowStatus.ACCRUED.map((unit) => (
                  <div key={unit.unitId} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">{unit.unitName}</p>
                      <p className="text-xs text-gray-500">{unit.projectName} • Awaiting escrow release & RERA payment</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-white font-medium">
                        {formatINR(unit.brokerage?.inflow?.developerOwedAmount)}
                      </span>
                      <StatusBadge status="ACCRUED" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RERA Payment Done - Ready to Generate Invoice */}
          <div className="bg-white/5 border border-white/10">
            <div className="px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-medium text-orange-400 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                RERA Paid - Ready to Generate Invoice ({unitsByInflowStatus.RERA_PAYMENT_DONE.length})
              </h3>
            </div>
            {unitsByInflowStatus.RERA_PAYMENT_DONE.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No units ready for invoice generation
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {unitsByInflowStatus.RERA_PAYMENT_DONE.map((unit) => (
                  <div key={unit.unitId} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">{unit.unitName}</p>
                      <p className="text-xs text-gray-500">
                        {unit.projectName} • RERA Ref: {unit.brokerage?.inflow?.reraPaymentRef} • 
                        Paid: {formatDate(unit.brokerage?.inflow?.reraPaymentConfirmedAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-white font-medium">
                        {formatINR(unit.brokerage?.inflow?.developerOwedAmount)}
                      </span>
                      <Button
                        size="sm"
                        onClick={() => generateDevInvoice(unit.unitId)}
                        disabled={processingId === unit.unitId}
                        className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border-orange-500/30"
                        data-testid={`generate-dev-invoice-${unit.unitId}`}
                      >
                        {processingId === unit.unitId ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <FileText className="w-3 h-3 mr-1" />
                            Generate Invoice
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Invoice Sent - Awaiting Payment */}
          <div className="bg-white/5 border border-white/10">
            <div className="px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-medium text-purple-400 flex items-center gap-2">
                <Send className="w-4 h-4" />
                Invoice Sent - Awaiting Developer Payment ({unitsByInflowStatus.DEV_INVOICED.length})
              </h3>
            </div>
            {unitsByInflowStatus.DEV_INVOICED.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No invoices awaiting payment
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {unitsByInflowStatus.DEV_INVOICED.map((unit) => (
                  <div key={unit.unitId} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">{unit.unitName}</p>
                      <p className="text-xs text-gray-500">
                        Invoice: {unit.brokerage?.inflow?.invoiceId} • 
                        Sent: {formatDate(unit.brokerage?.inflow?.invoicedAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-white font-medium">
                        {formatINR(unit.brokerage?.inflow?.invoiceAmount)}
                      </span>
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedUnit(unit);
                          setPaymentForm({
                            reference: '',
                            amount: unit.brokerage?.inflow?.invoiceAmount || ''
                          });
                          setShowDevPayModal(true);
                        }}
                        className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border-purple-500/30"
                        data-testid={`mark-dev-paid-${unit.unitId}`}
                      >
                        <CreditCard className="w-3 h-3 mr-1" />
                        Mark Paid
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Legacy: Invoice Sent - Awaiting Payment (old status) */}
          {unitsByInflowStatus.INVOICED.length > 0 && (
          <div className="bg-white/5 border border-white/10">
            <div className="px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-medium text-purple-400 flex items-center gap-2">
                <Send className="w-4 h-4" />
                Invoice Sent - Awaiting Payment (Legacy) ({unitsByInflowStatus.INVOICED.length})
              </h3>
            </div>
            <div className="divide-y divide-white/5">
              {unitsByInflowStatus.INVOICED.map((unit) => (
                <div key={unit.unitId} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white">{unit.unitName}</p>
                    <p className="text-xs text-gray-500">
                      Invoice: {unit.brokerage?.inflow?.invoiceId} • 
                      Sent: {formatDate(unit.brokerage?.inflow?.invoicedAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-white font-medium">
                      {formatINR(unit.brokerage?.inflow?.invoiceAmount)}
                    </span>
                    <Button
                      size="sm"
                      onClick={() => {
                        setSelectedUnit(unit);
                        setPaymentForm({
                          reference: '',
                          amount: unit.brokerage?.inflow?.invoiceAmount || ''
                        });
                        setShowDevPayModal(true);
                      }}
                      className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border-purple-500/30"
                    >
                      <CreditCard className="w-3 h-3 mr-1" />
                      Mark Paid
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Developer Brokerage Paid */}
          <div className="bg-white/5 border border-white/10">
            <div className="px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-medium text-emerald-400 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Developer Brokerage Paid ({unitsByInflowStatus.DEV_BROKERAGE_PAID.length})
              </h3>
            </div>
            {unitsByInflowStatus.DEV_BROKERAGE_PAID.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No developer payments received yet
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {unitsByInflowStatus.DEV_BROKERAGE_PAID.map((unit) => (
                  <div key={unit.unitId} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">{unit.unitName}</p>
                      <p className="text-xs text-gray-500">
                        Ref: {unit.brokerage?.inflow?.paymentReference} • 
                        Paid: {formatDate(unit.brokerage?.inflow?.paidAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-emerald-400 font-medium">
                        {formatINR(unit.brokerage?.inflow?.paymentAmount)}
                      </span>
                      <StatusBadge status="DEV_BROKERAGE_PAID" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Legacy Paid */}
          {unitsByInflowStatus.PAID.length > 0 && (
          <div className="bg-white/5 border border-white/10">
            <div className="px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-medium text-emerald-400 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Payment Received (Legacy) ({unitsByInflowStatus.PAID.length})
              </h3>
            </div>
            <div className="divide-y divide-white/5">
              {unitsByInflowStatus.PAID.map((unit) => (
                <div key={unit.unitId} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white">{unit.unitName}</p>
                    <p className="text-xs text-gray-500">
                      Ref: {unit.brokerage?.inflow?.paymentReference} • 
                      Paid: {formatDate(unit.brokerage?.inflow?.paidAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-emerald-400 font-medium">
                      {formatINR(unit.brokerage?.inflow?.paymentAmount)}
                    </span>
                    <StatusBadge status="PAID" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
      )}

      {/* =================================================================== */}
      {/* OUTFLOW TAB */}
      {/* =================================================================== */}
      {activeTab === 'outflow' && (
        <div className="space-y-6">
          {/* Booked - Awaiting Developer Payment */}
          <div className="bg-white/5 border border-white/10">
            <div className="px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-medium text-gray-400 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Booked - Awaiting Developer Payment ({allocationsByStatus.BOOKED.length})
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Commission recorded. Waiting for developer to pay platform brokerage.
              </p>
            </div>
            {allocationsByStatus.BOOKED.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No commissions awaiting developer payment
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {allocationsByStatus.BOOKED.map((item, idx) => (
                  <div key={idx} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">{item.allocation?.brokerName || item.allocation?.brokerId}</p>
                      <p className="text-xs text-gray-500">
                        {item.unitName} • {item.projectName} • 
                        Tier: {item.allocation?.tierAtInvestment} ({item.allocation?.tierRateUV}%)
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <span className="text-sm text-white font-medium">
                          {formatINR(item.allocation?.commissionFinal)}
                        </span>
                        <p className="text-xs text-gray-500">
                          Booked: {formatDate(item.allocation?.bookedAt)}
                        </p>
                      </div>
                      <StatusBadge status="BOOKED" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Awaiting Broker Invoice */}
          <div className="bg-white/5 border border-white/10">
            <div className="px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-medium text-blue-400 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Eligible - Awaiting Broker Invoice ({allocationsByStatus.ELIGIBLE.length})
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Developer has paid. Brokers can now submit invoices.
              </p>
            </div>
            {allocationsByStatus.ELIGIBLE.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No brokers awaiting invoice submission
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {allocationsByStatus.ELIGIBLE.map((item, idx) => (
                  <div key={idx} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">{item.allocation?.brokerName || item.allocation?.brokerId}</p>
                      <p className="text-xs text-gray-500">
                        {item.unitName} • {item.projectName} • 
                        Tier: {item.allocation?.tierAtInvestment} ({item.allocation?.tierRateUV}%)
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <span className="text-sm text-white font-medium">
                          {formatINR(item.allocation?.commissionFinal)}
                        </span>
                        <p className="text-xs text-gray-500">
                          Eligible since {formatDate(item.allocation?.eligibleAt)}
                        </p>
                      </div>
                      <StatusBadge status="ELIGIBLE" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pending Approval */}
          <div className="bg-white/5 border border-white/10">
            <div className="px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-medium text-purple-400 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Pending Approval ({allocationsByStatus.INVOICED.length})
              </h3>
            </div>
            {allocationsByStatus.INVOICED.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No invoices pending approval
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {allocationsByStatus.INVOICED.map((item, idx) => (
                  <div key={idx} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">{item.allocation?.brokerName || item.allocation?.brokerId}</p>
                      <p className="text-xs text-gray-500">
                        {item.unitName} • Invoice: {item.allocation?.invoiceId}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-white font-medium">
                        {formatINR(item.allocation?.commissionFinal)}
                      </span>
                      <Button
                        size="sm"
                        onClick={() => approveBrokerInvoice(item.allocation?.invoiceId)}
                        disabled={processingId === item.allocation?.invoiceId}
                        className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border-purple-500/30"
                        data-testid={`approve-broker-invoice-${item.allocation?.invoiceId}`}
                      >
                        {processingId === item.allocation?.invoiceId ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Approve
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ready to Pay */}
          <div className="bg-white/5 border border-white/10">
            <div className="px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-medium text-cyan-400 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Approved - Ready to Pay ({allocationsByStatus.APPROVED.length})
              </h3>
            </div>
            {allocationsByStatus.APPROVED.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No invoices ready for payment
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {allocationsByStatus.APPROVED.map((item, idx) => (
                  <div key={idx} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">{item.allocation?.brokerName || item.allocation?.brokerId}</p>
                      <p className="text-xs text-gray-500">
                        {item.unitName} • Approved: {formatDate(item.allocation?.approvedAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-white font-medium">
                        {formatINR(item.allocation?.commissionFinal)}
                      </span>
                      <Button
                        size="sm"
                        onClick={() => payBrokerInvoice(item.allocation?.invoiceId)}
                        disabled={processingId === item.allocation?.invoiceId}
                        className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border-cyan-500/30"
                        data-testid={`pay-broker-invoice-${item.allocation?.invoiceId}`}
                      >
                        {processingId === item.allocation?.invoiceId ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Wallet className="w-3 h-3 mr-1" />
                            Pay Now
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Paid History */}
          <div className="bg-white/5 border border-white/10">
            <div className="px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-medium text-emerald-400 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Paid ({allocationsByStatus.PAID.length})
              </h3>
            </div>
            {allocationsByStatus.PAID.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No broker payments completed yet
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {allocationsByStatus.PAID.map((item, idx) => (
                  <div key={idx} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">{item.allocation?.brokerName || item.allocation?.brokerId}</p>
                      <p className="text-xs text-gray-500">
                        {item.unitName} • Ref: {item.allocation?.paymentReference} • 
                        Paid: {formatDate(item.allocation?.paidAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-emerald-400 font-medium">
                        {formatINR(item.allocation?.commissionFinal)}
                      </span>
                      <StatusBadge status="PAID" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* BY-UNIT TAB */}
      {/* =================================================================== */}
      {activeTab === 'by-unit' && (
        <div className="space-y-4">
          {inflowUnits.length === 0 ? (
            <div className="bg-white/5 border border-white/10 px-4 py-12 text-center text-gray-500">
              No units with brokerage data
            </div>
          ) : (
            inflowUnits.map((unit) => (
              <div key={unit.unitId} className="bg-white/5 border border-white/10">
                {/* Unit Header */}
                <div
                  className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-white/5"
                  onClick={() => setExpandedUnits(prev => ({ ...prev, [unit.unitId]: !prev[unit.unitId] }))}
                >
                  <div className="flex items-center gap-3">
                    {expandedUnits[unit.unitId] ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-white font-medium">{unit.unitName}</p>
                      <p className="text-xs text-gray-500">{unit.projectName}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Inflow</p>
                      <StatusBadge status={unit.brokerage?.inflow?.status || 'PENDING'} />
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Outflow</p>
                      <StatusBadge status={unit.brokerage?.outflow?.status || 'PENDING'} />
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Allocations</p>
                      <p className="text-sm text-white font-medium">
                        {unit.brokerage?.allocations?.length || 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedUnits[unit.unitId] && (
                  <div className="px-4 pb-4 space-y-4 border-t border-white/10 pt-4">
                    {/* Pool Info - with robust field fallback */}
                    <div className="grid grid-cols-4 gap-4 text-xs">
                      <div className="bg-white/5 p-3">
                        <p className="text-gray-500">Developer Owed</p>
                        <p className="text-white font-medium">
                          {formatINR(
                            unit.brokerage?.inflow?.developerOwedAmount ||
                            unit.brokerage?.inflow?.expectedAmount ||
                            unit.brokerage?.inflow?.invoiceAmount ||
                            0
                          )}
                        </p>
                        <p className="text-gray-500">
                          {(unit.brokerage?.configSnapshot?.developerRateUV || 
                            unit.brokerage?.pool?.developerOwedRateUV || 
                            unit.brokerage?.pool?.rate || 
                            5)}% of UV
                        </p>
                      </div>
                      <div className="bg-white/5 p-3">
                        <p className="text-gray-500">Broker Pool</p>
                        <p className="text-white font-medium">
                          {formatINR(
                            unit.brokerage?.pool?.brokerPoolAmount ||
                            unit.brokerage?.pool?.amount ||
                            0
                          )}
                        </p>
                        <p className="text-gray-500">
                          {(unit.brokerage?.configSnapshot?.brokerPoolRateUV || 
                            unit.brokerage?.pool?.brokerPoolRateUV || 
                            unit.brokerage?.pool?.rate || 
                            2)}% of UV
                        </p>
                      </div>
                      <div className="bg-white/5 p-3">
                        <p className="text-gray-500">Total Booked</p>
                        <p className="text-white font-medium">
                          {formatINR(
                            unit.brokerage?.outflow?.totalBooked ||
                            unit.brokerage?.pool?.allocatedAmount ||
                            0
                          )}
                        </p>
                      </div>
                      <div className="bg-white/5 p-3">
                        <p className="text-gray-500">Leftover to Company</p>
                        <p className="text-emerald-400 font-medium">
                          {formatINR(
                            unit.brokerage?.outflow?.leftoverToCompany ||
                            unit.brokerage?.pool?.leftover ||
                            0
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Allocations Table */}
                    {unit.brokerage?.allocations?.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Broker Allocations</p>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-gray-500 border-b border-white/10">
                              <th className="text-left py-2">Broker</th>
                              <th className="text-left py-2">Tier</th>
                              <th className="text-right py-2">Capital</th>
                              <th className="text-right py-2">Commission</th>
                              <th className="text-center py-2">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {unit.brokerage.allocations.map((alloc, idx) => (
                              <tr key={idx} className="border-b border-white/5">
                                <td className="py-2 text-white">{alloc.brokerName || alloc.brokerId}</td>
                                <td className="py-2 text-gray-400">
                                  {alloc.tierAtInvestment} ({alloc.tierRateUV}%)
                                </td>
                                <td className="py-2 text-right text-white">{formatINR(alloc.capitalRaised)}</td>
                                <td className="py-2 text-right text-white">{formatINR(alloc.commissionFinal)}</td>
                                <td className="py-2 text-center">
                                  <StatusBadge status={alloc.outflowStatus} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => recomputeUnit(unit.unitId)}
                        disabled={processingId === unit.unitId}
                        className="border-white/20 text-gray-400 hover:bg-white/10"
                      >
                        <RefreshCw className={`w-3 h-3 mr-1 ${processingId === unit.unitId ? 'animate-spin' : ''}`} />
                        Recompute
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* =================================================================== */}
      {/* MODALS */}
      {/* =================================================================== */}

      {/* Mark Developer Paid Modal */}
      {showDevPayModal && selectedUnit && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-white/10 p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-white mb-4">
              Record Developer Payment
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              {selectedUnit.unitName} - Invoice: {selectedUnit.brokerage?.inflow?.invoiceId}
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 block mb-1">Payment Reference (UTR/NEFT Ref)</label>
                <input
                  type="text"
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, reference: e.target.value }))}
                  className="w-full bg-white/5 border border-white/20 px-3 py-2 text-white"
                  placeholder="Enter transaction reference"
                  data-testid="dev-payment-reference-input"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Amount Received (₹)</label>
                <input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full bg-white/5 border border-white/20 px-3 py-2 text-white"
                  placeholder="Enter amount"
                  data-testid="dev-payment-amount-input"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDevPayModal(false);
                  setSelectedUnit(null);
                }}
                className="flex-1 border-white/20 text-gray-400"
              >
                Cancel
              </Button>
              <Button
                onClick={markDevPaid}
                disabled={processingId || !paymentForm.reference || !paymentForm.amount}
                className="flex-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border-emerald-500/30"
                data-testid="confirm-dev-payment-btn"
              >
                {processingId ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  'Confirm Payment'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
